/**
 * Destinataire d'une course (migration 0028) — celui qui REÇOIT le colis, quand ce n'est
 * pas le client qui commande.
 *
 * ⚠️ TROIS copies de la même règle, à garder identiques :
 *   • ici (console opérateur) et `web/webapp-ts/src/api/destinataire.ts` (portail SaaS) ;
 *   • `supabase/functions/_shared/destinataire.ts` (edge function `create-course`) ;
 *   • la contrainte `courses_recipient_phone_chk` de la base (0028), qui refuse de toute
 *     façon : la console opérateur écrit DIRECTEMENT dans `courses`.
 * `destinataire.test.ts` rejoue les vecteurs des scénarios SQL (11.6 / 11.7).
 */

export const NOM_DESTINATAIRE_MAX = 120
export const TEL_DESTINATAIRE_MAX = 30
/** Chiffres, espaces, `+` en tête (ou après une parenthèse ouvrante), séparateurs « ( ) . - ». */
export const FORMAT_TEL = /^\(?\+?[0-9 ().-]+$/

/** 8 à 15 chiffres (E.164 ; 8 = un numéro local malien), 30 caractères au plus, format restreint. */
export function telephoneDestinataireValide(t: string): boolean {
  if (t.length > TEL_DESTINATAIRE_MAX || !FORMAT_TEL.test(t)) return false
  const chiffres = t.replace(/[^0-9]/g, '').length
  return chiffres >= 8 && chiffres <= 15
}

/** Ce que saisit le formulaire : la case « Le client reçoit lui-même » (cochée par défaut) et deux champs. */
export interface SaisieDestinataire {
  clientRecoit: boolean
  nom: string
  telephone: string
}

export const SAISIE_DESTINATAIRE_VIDE: SaisieDestinataire = { clientRecoit: true, nom: '', telephone: '' }

export interface Destinataire {
  nom: string | null
  telephone: string | null
}

/** Problèmes de saisie (vide = valide). Case cochée : rien à vérifier, les champs sont ignorés. */
export function verifierDestinataire(s: SaisieDestinataire): string[] {
  if (s.clientRecoit) return []
  const nom = s.nom.trim()
  const tel = s.telephone.trim()
  const pb: string[] = []
  if (!nom && !tel) {
    pb.push("Indiquez le nom ou le téléphone du destinataire, ou cochez « Le client reçoit lui-même ».")
    return pb
  }
  if (nom.length > NOM_DESTINATAIRE_MAX) pb.push(`Le nom du destinataire est limité à ${NOM_DESTINATAIRE_MAX} caractères.`)
  if (tel && !telephoneDestinataireValide(tel)) {
    pb.push(
      "Le téléphone du destinataire n'est pas valide : 8 à 15 chiffres, « + » seulement en tête (ex. +223 76 00 00 02).",
    )
  }
  return pb
}

/**
 * Ce qui part à la base : `null` quand le client reçoit lui-même (ou que rien n'est saisi) —
 * jamais de chaîne vide, que la contrainte de la base refuserait.
 */
export function destinataireDepuisSaisie(s: SaisieDestinataire): Destinataire | null {
  if (s.clientRecoit) return null
  const nom = s.nom.trim()
  const telephone = s.telephone.trim()
  if (!nom && !telephone) return null
  return { nom: nom || null, telephone: telephone || null }
}

/**
 * Qui joindre pour cette course : le destinataire s'il a un téléphone, sinon le client.
 * Même règle que le bouton WhatsApp et l'envoi automatique (`choisirCible`).
 */
export function cibleAppel(c: {
  recipient_name?: string | null
  recipient_phone?: string | null
  client_phone?: string | null
}): { telephone: string | null; destinataire: boolean } {
  const tel = (v: string | null | undefined) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null)
  const d = tel(c.recipient_phone)
  return d ? { telephone: d, destinataire: true } : { telephone: tel(c.client_phone), destinataire: false }
}

/** `tel:+22376000002` pour le bouton « Appeler » ; `null` si le numéro n'a aucun chiffre. */
export function lienAppel(telephone: string | null | undefined): string | null {
  if (!telephone) return null
  const t = telephone.trim()
  const chiffres = t.replace(/[^0-9]/g, '')
  if (!chiffres) return null
  return `tel:${t.startsWith('+') || t.startsWith('(+') ? '+' : ''}${chiffres}`
}
